import { useMemo, useRef, useState, useCallback } from "react";
import { Skeleton } from "primereact/skeleton";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "../../admin/components/shared/ConfirmDialog";
import { useCartContext, useAuthContext, useWarehouseContext } from "../../../app/providers/contextIndex";
import AppToast from "../../../shared/components/AppToast";
import { existenciaCotizacion, abrirCotizacionPDF } from "../../../services/backend/cotizacionService";
import Loader from "../../../shared/ui/LoaderTransparent.js";
import CartItem from "../components/CartItem";
import CartSummary from "../components/CartSummary";
import RelatedProductsPanel from "../components/RelatedProductsPanel";
import CheckoutFlowStepper, { CHECKOUT_FLOW_STEPS } from "../../checkout/components/CheckoutFlowStepper";
import { useCartEffects } from "../hooks/useCartEffects";
import useCartQuantityHandlers from "../hooks/useCartQuantityHandlers";
import useCartItemRemoval from "../hooks/useCartItemRemoval";
import useCheckoutNavigation from "../hooks/useCheckoutNavigation";
import useRelatedProducts from "../hooks/useRelatedProducts";
import { formatResumenValue, sumCartTotal } from "../../../services/transforms/cartDataTransforms";
import { CLP_FORMATTER } from "../../../utils/currency";
import { getCompany } from "../../../config/companyConfig";
import "../styles/cart.css";

const Cart = () => {
    const {
        cartItems,
        cartResumen,
        cotizacionId,
        addToCart,
        updateCartQuantityFromBackend,
        loadCotizacionFromBackend,
        isCartLoading,
    } = useCartContext();
    const { appUser } = useAuthContext();
    const { selectedAlmacen } = useWarehouseContext();

    const [downloadingCotizacion, setDownloadingCotizacion] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const confirmResolveRef = useRef(null);
    const { selectedLineKeys } = useCartEffects(cartItems);
    const toastRef = useRef(null);
    const loading = isCartLoading && cartItems.length === 0;

    const confirmAsync = useCallback((message) => {
        return new Promise((resolve) => {
            confirmResolveRef.current = resolve;
            setConfirmMessage(message);
            setConfirmVisible(true);
        });
    }, []);

    const handleConfirmAccept = useCallback(() => {
        setConfirmVisible(false);
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        if (resolve) resolve(true);
    }, []);

    const handleConfirmReject = useCallback(() => {
        setConfirmVisible(false);
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        if (resolve) resolve(false);
    }, []);

    const { handleRemoveItem } = useCartItemRemoval({
        cartItems,
        loadCotizacionFromBackend,
        selectedAlmacen,
        toastRef,
    });

    const {
        updatingQtyLineKey,
        handleDecreaseQty,
        handleIncreaseQty,
        handleChangeQty,
        isUpdatingQty,
    } = useCartQuantityHandlers({ cartItems, updateCartQuantityFromBackend, toastRef, confirm: confirmAsync, onRemoveItem: handleRemoveItem });

    const {
        continueLoading,
        handleContinueCheckout,
    } = useCheckoutNavigation({ cotizacionId, appUser, toastRef });

    const {
        filteredRelatedItems,
        relatedLoading,
        relatedError,
        relatedAddStatus,
        handleAddRelatedProduct,
    } = useRelatedProducts({ cotizacionId, cartItems, cartResumen, addToCart, toastRef });

    const selectedItems = useMemo(() => {
        const selectedSet = new Set(selectedLineKeys);
        return cartItems.filter((item) => selectedSet.has(item.lineKey));
    }, [cartItems, selectedLineKeys]);
    const selectedTotal = useMemo(
        () => sumCartTotal(selectedItems),
        [selectedItems]
    );
    const selectedUnits = useMemo(
        () => selectedItems.reduce((acc, item) => acc + Number(item.quantity || 0), 0),
        [selectedItems]
    );
    const formattedSelectedTotal = useMemo(() => CLP_FORMATTER.format(selectedTotal || 0), [selectedTotal]);
    const isPageLoading = loading;
    const isCartBusy = (isCartLoading && cartItems.length > 0) || Boolean(updatingQtyLineKey) || relatedAddStatus === "loading";

    const handleDownloadCotizacion = async () => {
        if (!cotizacionId || downloadingCotizacion) {
            return;
        }
        setDownloadingCotizacion(true);
        try {
            await existenciaCotizacion(cotizacionId);
            abrirCotizacionPDF(cotizacionId);
        } catch (error) {
            toastRef.current?.show({
                severity: "error",
                summary: "No se pudo descargar",
                detail: error?.message || "La cotización ya no está disponible.",
                life: 3200,
            });
        } finally {
            setDownloadingCotizacion(false);
        }
    };

    return (
        <div className="landing-container cart-page">
            <AppToast ref={toastRef} />
            <ConfirmDialog
                visible={confirmVisible}
                header="Confirmar eliminación"
                message={confirmMessage}
                acceptLabel="Eliminar"
                rejectLabel="Cancelar"
                acceptClassName="p-button-danger"
                onCancel={handleConfirmReject}
                onConfirm={handleConfirmAccept}
            />
            <CheckoutFlowStepper steps={CHECKOUT_FLOW_STEPS} currentStepIndex={0} />
            <div className="cart-layout">
                <section className="cart-items" style={{ position: "relative" }}>
                    {isCartBusy ? (
                        <Loader
                            coverParent
                            status={relatedAddStatus !== "idle" ? relatedAddStatus : "loading"}
                            loadingMessage="Actualizando carro..."
                            successMessage="Listo"
                            errorMessage="Ocurrió un error"
                        />
                    ) : null}
                    {isPageLoading ? (
                        <div className="border-round border-1 surface-border p-4">
                            <ul className="m-0 p-0 list-none">
                                {[...Array(4)].map((_, i) => (
                                    <li key={i} className={i < 3 ? "mb-3" : ""}>
                                        <div className="flex">
                                            <Skeleton  size="4rem" className="mr-2" />
                                            <div style={{ flex: "1" }}>
                                                <Skeleton width="100%" className="mb-2" />
                                                <Skeleton width="75%" />
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    {!isPageLoading && !cartItems.length ? (
                        <div className="cart-empty">
                            <span>No hay productos en el carro.</span>
                            <Link to="/catalogo">Ver productos</Link>
                        </div>
                    ) : null}
                    {!isPageLoading && cartItems.length ? cartItems.map((item) => (
                        <CartItem
                            key={item.lineKey}
                            item={item}
                            lineKey={item.lineKey}
                            currencyFormatter={CLP_FORMATTER}
                            onDecreaseQty={handleDecreaseQty}
                            onIncreaseQty={handleIncreaseQty}
                            onChangeQty={handleChangeQty}
                            onRemove={handleRemoveItem}
                            isUpdatingQty={isUpdatingQty(item.lineKey)}
                        />
                    )) : null}
                    {!isPageLoading && cartItems.length >= 4 ? (
                        <RelatedProductsPanel
                            relatedLoading={relatedLoading}
                            relatedItems={filteredRelatedItems}
                            relatedError={relatedError}
                            currencyFormatter={CLP_FORMATTER}
                            onAddToCart={handleAddRelatedProduct}
                        />
                    ) : null}
                </section>
                <CartSummary
                    resumenData={cartResumen}
                    selectedUnits={selectedUnits}
                    formattedSelectedTotal={formattedSelectedTotal}
                    selectedItems={selectedItems}
                    selectedTotal={selectedTotal}
                    formatResumenValue={(value, fallback) => formatResumenValue(value, CLP_FORMATTER, fallback)}
                    currencyFormatter={CLP_FORMATTER}
                    onContinue={handleContinueCheckout}
                    isContinuing={continueLoading}
                    downloadingCotizacion={downloadingCotizacion}
                    onDownloadCotizacion={handleDownloadCotizacion}
                    onOpenSpecialistWhatsApp={() => window.open(getCompany().whatsappUrl, "_blank", "noopener,noreferrer")}
                />
            </div>
        </div>
    );
};

export default Cart;
